"use client";
import { X } from "lucide-react";

import * as React from "react";
import {  motion } from "framer-motion";
import { SearchSVG } from "@/components/ui/SVG";
import DotPulseLdr from "@/components/ui/DotPulseLdr";
import OverlayWrapper from "@/components/ui/OverLayWrapper";
import { CustomModal } from "@/components/ui/CustomModal";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import Image from "next/image";
import { cn, getProducerLabel, getProducerKey } from "../utils";
import { useDebouncedOptions } from "../hooks";
import type { ProductPickerItem, SelectedProduct } from "../types";
import { Check } from "lucide-react";

export function ProductPickerOverlay({
  selectedProducts,
  onToggleProduct,
  onClose,
}: {
  selectedProducts: SelectedProduct[];
  onToggleProduct: (option: ProductPickerItem) => void;
  onClose: () => void;
}) {
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const [search, setSearch] = React.useState("");
  const [producerFilter, setProducerFilter] = React.useState<string | null>(
    null,
  );
  const fetchProducts = React.useCallback(async (searchTerm: string) => {
    const params = new URLSearchParams();
    params.set("take", "500");
    const trimmed = searchTerm.trim();
    if (trimmed) {
      params.set("q", trimmed);
    }
    const res = await fetch(`/api/products/picker?${params.toString()}`);
    if (!res.ok) {
      throw new Error("Failed to load products");
    }
    const json = await res.json();
    return Array.isArray(json?.products) ? json.products : [];
  }, []);
  const { results: items, loading, error } = useDebouncedOptions<ProductPickerItem>(
    {
      query: search,
      delay: 300,
      fetcher: fetchProducts,
    },
  );
  const selectedIds = React.useMemo(
    () => new Set(selectedProducts.map((product) => product.productId)),
    [selectedProducts],
  );
  const producerOptions = React.useMemo(() => {
    const map = new Map<
      string,
      { value: string; label: string; count: number }
    >();
    items.forEach((item) => {
      const key = getProducerKey(item.producer);
      const existing = map.get(key);
      if (existing) {
        existing.count += 1;
      } else {
        map.set(key, {
          value: key,
          label: getProducerLabel(item.producer),
          count: 1,
        });
      }
    });
    return Array.from(map.values()).sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: "base" }),
    );
  }, [items]);

  const activeProducer = React.useMemo(() => {
    if (!producerOptions.length) return null;
    if (
      producerFilter &&
      producerOptions.some((option) => option.value === producerFilter)
    ) {
      return producerFilter;
    }
    return producerOptions[0]?.value ?? null;
  }, [producerFilter, producerOptions]);

  const filteredItems = React.useMemo(
    () =>
      !activeProducer
        ? items
        : items.filter(
            (item) => getProducerKey(item.producer) === activeProducer,
          ),
    [items, activeProducer],
  );
  const activeProducerLabel = React.useMemo(() => {
    if (!activeProducer) return null;
    return (
      producerOptions.find((option) => option.value === activeProducer)
        ?.label ?? "this producer"
    );
  }, [activeProducer, producerOptions]);
  const emptyMessage = React.useMemo(() => {
    if (search.trim()) {
      return activeProducerLabel
        ? `No ${activeProducerLabel} products match that search.`
        : "No products match that search.";
    }
    return activeProducerLabel
      ? `No products found for ${activeProducerLabel}.`
      : "No products found.";
  }, [search, activeProducerLabel]);

  React.useEffect(() => {
    if (error) {
      console.warn("product picker fetch failed", error);
    }
  }, [error]);

  const body = (
    <div className="flex flex-col">
        <div className="sticky top-0 z-10 bg-white px-4 py-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-lg font-semibold text-gray-900">
                Product library
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="flex h-9 w-9 items-center justify-center rounded-full bg-white text-slateCustom transition hover:text-pinkCustom"
            >
              <X className="h-4 w-4" strokeWidth={4} />
            </button>
          </div>
        </div>

        <div className="sticky top-14 z-10 h-16 border-b border-gray-200 bg-white px-4 py-3">
          <SearchSVG className="absolute top-1/2 left-7 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="search"
            className="focus:border-pinkCustom w-full rounded-md border border-gray-200 bg-white py-2 pr-3 pl-9 text-gray-800 focus:outline-none"
            placeholder="Search by part number…"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
        {producerOptions.length > 0 ? (
          <div className="sticky top-30 z-10 bg-white">
            <div
              className="flex max-w-full overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
              role="tablist"
              aria-label="Filter products by producer"
            >
              {producerOptions.map((option) => (
                <button
                  type="button"
                  key={option.value}
                  onClick={() => setProducerFilter(option.value)}
                  className={cn(
                    "w-1/2 border-b-4 p-3 text-sm whitespace-nowrap transition",
                    activeProducer === option.value
                      ? "border-pinkCustom text-pinkCustom"
                      : "border-gray-200 text-gray-600 hover:border-gray-300",
                  )}
                  role="tab"
                  aria-selected={activeProducer === option.value}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        ) : null}

        <div className="flex-1 bg-gray-50 p-4 pb-30">
          {loading ? (
            <div className="flex justify-center py-8">
              <DotPulseLdr color="pinkCustom" />
            </div>
          ) : filteredItems.length > 0 ? (
            <ul className="space-y-3">
              {filteredItems.map((item) => {
                const selected = selectedIds.has(item.id);
                return (
                  <li key={item.id}>
                    <motion.button
                      type="button"
                      onClick={() => onToggleProduct(item)}
                      className={cn(
                        "flex w-full items-center gap-3 rounded-xl border px-3 py-2 text-left transition",
                        selected
                          ? "border-pinkCustom bg-pinkCustom/10"
                          : "border-gray-200 bg-white hover:border-gray-300",
                      )}
                      whileTap={{ scale: 0.9 }}
                    >
                      <div className="relative h-16 w-16 overflow-hidden rounded border border-gray-100 bg-white">
                        {item.hero_image ? (
                          <Image
                            src={item.hero_image}
                            alt={item.part_number}
                            width={64}
                            height={64}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center text-[10px] text-gray-400 uppercase">
                            No image
                          </div>
                        )}
                        {selected ? (
                          <span className="absolute inset-0 flex items-center justify-center bg-black/50 text-white">
                            <Check className="h-6 w-6" />
                          </span>
                        ) : null}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-semibold text-gray-900">
                          {item.part_number}
                        </p>
                        <p className="truncate text-xs text-gray-500">
                          {getProducerLabel(item.producer)}
                        </p>
                      </div>
                    </motion.button>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="py-10 text-center text-sm text-gray-500">
              {emptyMessage}
            </p>
          )}
        </div>
    </div>
  );

  if (isDesktop) {
    return (
      <CustomModal
        open
        onOpenChange={(next) => {
          if (!next) onClose();
        }}
        maxWidth="max-w-5xl"
        maxHeight="90vh"
        bodyClassName="p-0"
      >
        {body}
      </CustomModal>
    );
  }

  return (
    <OverlayWrapper
      onClose={onClose}
      snapPoints={["92vh"]}
      className="bg-grayCustom"
      draggable={false}
    >
      {body}
    </OverlayWrapper>
  );
}
